import React, { useEffect, useState } from 'react';
import { Send, Paperclip, X, Check } from 'lucide-react';
import { Button } from '../ui/button';
import FileDropZone from '../FileDropZone';
import { useT } from '../../contexts/LocaleProvider';

/* Posts ship in up to three languages · FR is the canonical one (also
 * mirrored to the legacy title/body columns). Readers see the version
 * matching their UI locale; missing translations fall back to FR. */
const LANGS = ['fr', 'nl', 'en'];

const emptyTranslations = () => ({
  fr: { title: '', body: '' },
  nl: { title: '', body: '' },
  en: { title: '', body: '' },
});

const emptyFor = (defaultCategory = 'news') => ({
  translations: emptyTranslations(),
  category: defaultCategory,
  pinned: false,
  cta_label: '',
  cta_url: '',
  video_url: '',
});

const fmtSize = (n) => {
  if (!n) return '';
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} kB`;
  return `${(n / (1024 * 1024)).toFixed(1)} MB`;
};

/* Pull existing post values into the new translations shape. Legacy posts
 * have only `title`/`body` set · treat those as the FR copy. */
const hydrate = (initialPost, defaultCategory) => {
  if (!initialPost) return emptyFor(defaultCategory);
  const base = emptyTranslations();
  const provided = initialPost.translations || {};
  for (const lang of LANGS) {
    base[lang] = {
      title: provided[lang]?.title ?? (lang === 'fr' ? (initialPost.title || '') : ''),
      body:  provided[lang]?.body  ?? (lang === 'fr' ? (initialPost.body  || '') : ''),
    };
  }
  return {
    translations: base,
    category: initialPost.category || defaultCategory,
    pinned: !!initialPost.pinned,
    cta_label: initialPost.cta_label || '',
    cta_url: initialPost.cta_url || '',
    video_url: initialPost.video_url || '',
  };
};

export default function AnnouncementComposer({
  open,
  onPublish,
  onClose,
  defaultCategory = 'news',
  initialPost = null,        // when provided · switches to edit mode
  title: titleOverride = null,
}) {
  const t = useT();
  const isEdit = !!initialPost;
  const [post, setPost] = useState(() => hydrate(initialPost, defaultCategory));
  const [activeLang, setActiveLang] = useState('fr');
  const [files, setFiles] = useState([]);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  // Cross-post to #be-team on Slack. Default true for new posts (every
  // announcement is broadcast unless the author opts out); for edits we
  // leave it false so re-saving doesn't double-post into Slack.
  const [postToSlack, setPostToSlack] = useState(!isEdit);

  // Keep the form in sync when the parent swaps which post is being edited.
  useEffect(() => {
    if (initialPost) {
      setPost(hydrate(initialPost, defaultCategory));
      setActiveLang('fr');
      setFiles([]);
      setError('');
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialPost?.id]);

  if (!open) return null;

  const addFiles = (fs) => setFiles(prev => [...prev, ...fs]);
  const removeFile = (i) => setFiles(prev => prev.filter((_, idx) => idx !== i));

  const current = post.translations[activeLang] || { title: '', body: '' };
  const setCurrent = (patch) => setPost(p => ({
    ...p,
    translations: {
      ...p.translations,
      [activeLang]: { ...p.translations[activeLang], ...patch },
    },
  }));

  /* Pack the form into the API payload. The legacy `title`/`body` columns
   * stay the canonical FR copy so older code paths keep working. */
  const buildPayload = () => {
    const fr = post.translations.fr || { title: '', body: '' };
    // Strip empty translations so the JSON stays clean.
    const trimmed = {};
    for (const lang of LANGS) {
      const tr = post.translations[lang] || {};
      if ((tr.title || '').trim() || (tr.body || '').trim()) {
        trimmed[lang] = { title: tr.title || '', body: tr.body || '' };
      }
    }
    return {
      title: fr.title.trim(),
      body:  fr.body.trim(),
      translations: trimmed,
      category: post.category,
      pinned: post.pinned,
      cta_label: (post.cta_label || '').trim(),
      cta_url:   (post.cta_url   || '').trim(),
      video_url: (post.video_url || '').trim(),
      // Honoured on create only · the backend ignores the field on PATCH
      // (no double-posting on edit). Defaults to true server-side too,
      // so omitting the field on legacy clients still announces.
      post_to_slack: postToSlack,
    };
  };

  const submit = async () => {
    const payload = buildPayload();
    if (!payload.title || !payload.body) {
      setActiveLang('fr');
      setError(t('composer.required'));
      return;
    }
    setSaving(true);
    setError('');
    try {
      await onPublish(payload, files);
      if (!isEdit) {
        setPost(emptyFor(defaultCategory));
        setFiles([]);
        setActiveLang('fr');
      }
      onClose?.();
    } catch (e) {
      setError(e.response?.data?.detail || t('composer.publishFailed'));
    } finally {
      setSaving(false);
    }
  };

  const cancel = () => {
    if (!isEdit) {
      setPost(emptyFor(defaultCategory));
      setFiles([]);
      setActiveLang('fr');
    }
    setError('');
    onClose?.();
  };

  const headerTitle = titleOverride
    ?? (isEdit ? t('composer.editTitle') : t('home.publishPost'));
  const submitLabel = saving
    ? (isEdit ? t('composer.saving') : t('composer.publishing'))
    : (isEdit ? t('composer.save')   : t('composer.publish'));

  return (
    <section className="bg-[hsl(var(--surface))] border border-border rounded-lg overflow-hidden">
      <div className="px-5 py-3 flex items-center justify-between border-b border-border bg-[hsl(var(--bg-subtle))]">
        <span className="font-display text-[1rem]">{headerTitle}</span>
        <button type="button" onClick={cancel} className="text-[hsl(var(--text-muted))] hover:text-[hsl(var(--text))]" aria-label={t('common.cancel')}>
          <X className="h-4 w-4" />
        </button>
      </div>
      <div className="px-5 pb-5 pt-4 space-y-3">
          {error && (
            <div className="border border-[hsl(var(--danger)/.3)] bg-[hsl(var(--danger-soft))] text-[hsl(var(--danger))] px-3 py-2 text-sm rounded-md">
              {error}
            </div>
          )}

          {/* Language tabs · FR is required, NL/EN are optional fallbacks. */}
          <div className="flex items-center gap-1 border-b border-border -mt-1">
            {LANGS.map(lang => {
              const tr = post.translations[lang] || {};
              const filled = !!((tr.title || '').trim() || (tr.body || '').trim());
              const isActive = lang === activeLang;
              return (
                <button
                  key={lang}
                  type="button"
                  onClick={() => setActiveLang(lang)}
                  className={`relative inline-flex items-center gap-1.5 px-3 py-1.5 text-[13px] transition ${
                    isActive
                      ? 'text-[hsl(var(--text))] font-medium border-b-2 -mb-px border-[hsl(var(--accent))]'
                      : 'text-[hsl(var(--text-muted))] hover:text-[hsl(var(--text))]'
                  }`}
                >
                  <span className="uppercase font-num text-[11px]">{lang}</span>
                  <span>{t(`composer.lang.${lang}`)}</span>
                  {lang === 'fr' && <span className="text-[10px] opacity-70">★</span>}
                  {filled && lang !== 'fr' && <Check className="h-3 w-3 text-[hsl(var(--accent))]" />}
                </button>
              );
            })}
          </div>

          <div className="field">
            <label className="field-label">
              {t('composer.title')}
              {activeLang !== 'fr' && (
                <span className="ml-2 text-[11px] text-[hsl(var(--text-muted))]">
                  {t('composer.lang.optional')}
                </span>
              )}
            </label>
            <input className="input" value={current.title}
              onChange={e => setCurrent({ title: e.target.value })}
              placeholder={t('composer.title.ph')} />
          </div>
          <div className="field">
            <label className="field-label">{t('composer.body')}</label>
            <textarea rows={6} value={current.body}
              onChange={e => setCurrent({ body: e.target.value })}
              placeholder={t('composer.body.ph')} />
          </div>

          <FileDropZone
            onFiles={addFiles}
            compact
            label={t('composer.attachDrop')}
            sublabel={t('card.dropOrBrowse')}
          />

          {files.length > 0 && (
            <ul className="space-y-1.5">
              {files.map((f, i) => (
                <li key={i} className="flex items-center gap-2.5 px-3 py-2 border border-border rounded-md bg-[hsl(var(--bg-subtle))] text-[14px]">
                  <Paperclip className="h-4 w-4 text-[hsl(var(--text-soft))]" />
                  <span className="flex-1 truncate">{f.name}</span>
                  <span className="text-[12px] text-[hsl(var(--text-muted))] font-num">{fmtSize(f.size)}</span>
                  <button type="button" onClick={() => removeFile(i)} className="text-[hsl(var(--text-muted))] hover:text-[hsl(var(--danger))]" aria-label={t('common.delete')}>
                    <X className="h-3.5 w-3.5" />
                  </button>
                </li>
              ))}
            </ul>
          )}

          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <div className="field">
              <label className="field-label">{t('composer.cta.label') || 'Bouton · texte (optionnel)'}</label>
              <input className="input" value={post.cta_label}
                     onChange={e => setPost(p => ({ ...p, cta_label: e.target.value }))}
                     placeholder="Lire la suite" />
            </div>
            <div className="field">
              <label className="field-label">{t('composer.cta.url') || 'Bouton · URL'}</label>
              <input className="input font-num" value={post.cta_url}
                     onChange={e => setPost(p => ({ ...p, cta_url: e.target.value }))}
                     placeholder="https://" />
            </div>
          </div>

          <div className="field">
            <label className="field-label">{t('composer.video') || 'Vidéo · lien direct (optionnel)'}</label>
            <input className="input font-num" value={post.video_url}
                   onChange={e => setPost(p => ({ ...p, video_url: e.target.value }))}
                   placeholder="https://drive.lamptwist.be/api/public/f/…/video.mp4" />
          </div>

          <div className="flex items-end flex-wrap gap-3">
            <div className="field w-44">
              <label className="field-label">{t('composer.category')}</label>
              <select value={post.category} onChange={e => setPost(p => ({ ...p, category: e.target.value }))}>
                <option value="news">{t('category.news')}</option>
                <option value="hr">{t('category.hr')}</option>
                <option value="event">{t('category.event')}</option>
                <option value="reminder">{t('category.reminder')}</option>
              </select>
            </div>
            <label className="flex items-center gap-2 text-[14px] mb-1">
              <input type="checkbox" checked={post.pinned} onChange={e => setPost(p => ({ ...p, pinned: e.target.checked }))} />
              {t('composer.pin')}
            </label>
            {!isEdit && (
              <label className="flex items-center gap-2 text-[14px] mb-1"
                     title={t('composer.slack.hint')}>
                <input type="checkbox" checked={postToSlack}
                       onChange={e => setPostToSlack(e.target.checked)} />
                {t('composer.slack')}
              </label>
            )}
            <div className="flex-1" />
            <Button onClick={submit} disabled={saving} className="gap-2">
              <Send className="h-4 w-4" /> {submitLabel}
            </Button>
          </div>
      </div>
    </section>
  );
}
